import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState, } from "react";
import DOMPurify from "dompurify";
import "./_style.scss";
import useIsOverflowing from "hooks/useIsOverflowing";
import Comments from "widgets/Comments";
import useAuthViewer from "hooks/useAuthViewer";
import ImageCarousel from "./components/ImageCarousel/ImageCarousel";
import CampaignUpdateHeader from "./components/CampaignUpdateHeader/CampaignUpdateHeader";
import CampaignUpdateFooter from "./components/CampaignUpdateFooter/CampaignUpdateFooter";
import VideoPlayer from "./components/VideoPlayer/VideoPlayer";
function CampaignUpdate({ update, campaign, onClose, onReactionsChange, onCommentAdded, isCommentsEnabled, }) {
    var _a;
    const { viewer: user } = useAuthViewer();
    const hash = `#campaign-update-${update.id.slice(0, 8)}`;
    const articleRef = useRef(null);
    const footerRef = useRef(null);
    const commentsRef = useRef(null);
    const scrollToCommentsRef = useRef(false);
    const [showComments, setShowComments] = useState(() => window.location.hash === hash);
    const [expanded, setExpanded] = useState(false);
    const sanitizedContent = useMemo(() => DOMPurify.sanitize(update.content), [update.content]);
    const shouldClamp = useMemo(() => update.updateType !== "Text", [update.updateType]);
    const isClamped = shouldClamp && !expanded;
    const [contentRef, isOverflowing] = useIsOverflowing(isClamped);
    const showToggle = shouldClamp && (isOverflowing || expanded);
    useEffect(() => {
        if (window.location.hash === hash && articleRef.current) {
            articleRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [hash]);
    useEffect(() => {
        if (!scrollToCommentsRef.current) {
            return;
        }
        scrollToCommentsRef.current = false;
        if (commentsRef.current && footerRef.current) {
            const footerHeight = footerRef.current.getBoundingClientRect().height;
            commentsRef.current.style.scrollMarginTop = `${footerHeight}px`;
            commentsRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
    }, [showComments]);
    return (_jsxs("article", { className: "campaign-update", ref: articleRef, children: [_jsxs("div", { className: "campaign-update__main", children: [_jsx(CampaignUpdateHeader, { update: update, campaign: campaign, onClose: onClose }), update.updateType === "Image" && (_jsx(ImageCarousel, { update: update })), update.updateType === "Video" && (_jsx(VideoPlayer, { update: update })), _jsxs("div", { className: "campaign-update__body", children: [_jsx("h3", { className: "campaign-update__title", children: update.title }), _jsx("div", { ref: contentRef, className: `campaign-update__content${isClamped ? " campaign-update__content--clamped" : ""}`, dangerouslySetInnerHTML: { __html: sanitizedContent } }), showToggle && (_jsx("button", { type: "button", className: "campaign-update__see-more", onClick: () => setExpanded((e) => !e), children: expanded ? "See less" : "See more" }))] }), _jsx(CampaignUpdateFooter, { ref: footerRef, update: update, campaign: campaign, commentAnchorId: hash.slice(1), showComments: showComments, onToggleComments: () => setShowComments((wasShowingComments) => {
                            const willShowComments = !wasShowingComments;
                            scrollToCommentsRef.current = willShowComments;
                            return willShowComments;
                        }), onReactionsChange: onReactionsChange, isCommentsEnabled: isCommentsEnabled })] }), isCommentsEnabled && showComments && (_jsx("div", { className: "campaign-update__comments", ref: commentsRef, children: _jsx(Comments, { campaignId: campaign.id, campaignTitle: (_a = campaign.title) !== null && _a !== void 0 ? _a : "", campaignUpdateId: update.id, user: user !== null && user !== void 0 ? user : {}, onCommentAdded: onCommentAdded }) }))] }));
}
export default CampaignUpdate;
